import { User, GlobalConfig, Attachment } from '../../types';
import { TokenConfig, DEFAULT_TOKEN_CONFIG, loadTokenConfig, saveTokenConfig, getTokenConfig } from './config';
import { TokenChargeRequest, TokenChargeResult, QuickCostEstimate, estimateCost, calculateCharge } from './pricing';
import { TokenTransaction, chargeTokens, refundTokens, getTransactionHistory } from './transactions';
import { TokenStatus, getStatus, canSend, shouldShowWarningModal, getTokenRefreshAmount } from './validation';
import { getAnalytics, getTokenUsageReport } from './analytics';
import {
    calculateAttachmentTokenCost,
    TokenCostBreakdown
} from '../../utils/tokenCostCalculator';
import {
    calculateTokenCost as calculateAdvancedCost,
    analyzeComplexity,
    detectFeatures,
    CostBreakdown,
    CostCalculationInput
} from '../../utils/advancedTokenCostSystem';
import {
    refundTokens as systemRefund,
    shouldRefund,
    getRefundHistory,
    getTotalRefundedTokens
} from '../../utils/tokenRefundSystem';
import {
    hasUnlimitedTokens,
    getTokenMultiplier
} from '../../utils/promoEffectsManager';
import {
    getEffectiveTokenLimit,
    getTokenUsagePercentage,
    getTokenStatusText,
    shouldShowUpgradePrompt,
    canSendMessage
} from '../../utils/tokenManager';

// Re-export types
export type {
    TokenConfig,
    TokenChargeRequest,
    TokenChargeResult,
    TokenTransaction,
    TokenStatus,
    QuickCostEstimate,
    TokenCostBreakdown,
    CostBreakdown,
    CostCalculationInput
};

export { DEFAULT_TOKEN_CONFIG };

// Unified Token System Class (Facade for backward compatibility and ease of use)
class UnifiedTokenSystem {
    constructor() {
        loadTokenConfig();
    }

    getConfig(): TokenConfig {
        return getTokenConfig();
    }

    updateConfig(updates: Partial<TokenConfig>): void {
        saveTokenConfig(updates);
    }

    getStatus(user: User | null, globalConfig: GlobalConfig): TokenStatus {
        return getStatus(user, globalConfig);
    }

    canSend(user: User | null, globalConfig: GlobalConfig, estimatedCost: number): { canSend: boolean; reason?: string } {
        return canSend(user, globalConfig, estimatedCost);
    }

    getRefreshAmount(user: User | null, globalConfig: GlobalConfig): number {
        return getTokenRefreshAmount(user, globalConfig);
    }

    estimateCost(params: QuickCostEstimate): number {
        return estimateCost(params);
    }

    calculateAdvanced(input: CostCalculationInput): CostBreakdown {
        return calculateAdvancedCost(input);
    }

    calculateAttachmentCost(attachments: Attachment[], usePreAnalysis: boolean = false): TokenCostBreakdown {
        // Map Attachment to a mock File object
        const mockFiles = attachments.map(att => {
            const size = att.data ? Math.ceil((att.data.length * 3) / 4) : 0;
            return {
                name: att.name || 'attachment',
                size: size,
            } as unknown as File;
        });
        return calculateAttachmentTokenCost(mockFiles, '');
    }

    analyzeComplexity(text: string): 'simple' | 'moderate' | 'complex' | 'veryComplex' {
        return analyzeComplexity(text) as 'simple' | 'moderate' | 'complex' | 'veryComplex';
    }

    detectFeatures(text: string): ReturnType<typeof detectFeatures> {
        return detectFeatures(text, false);
    }

    async charge(
        request: TokenChargeRequest,
        user: User,
        updateUser: (updates: Partial<User>) => void,
        skipWarnings: boolean = false
    ): Promise<TokenChargeResult> {
        return chargeTokens(request, user, updateUser, skipWarnings);
    }

    async refund(
        user: User | null,
        amount: number,
        reason: 'error' | 'partial' | 'cancelled'
    ): Promise<{ refunded: number; newUsage: number }> {
        if (!user) return { refunded: 0, newUsage: 0 };

        // Use the new refundTokens function but adapt return type to match old signature if needed
        // The old refund returned { refunded, newUsage }, the new one is void but updates user via callback
        // Wait, the old one took a reason 'cancelled' which maps to 'manual' or similar?
        // Let's map 'cancelled' to 'manual' for now
        const mappedReason = reason === 'cancelled' ? 'manual' : reason as 'error' | 'partial' | 'manual';

        // We need a way to get the new usage back. 
        // The new refundTokens takes an updateUser callback.
        let updatedUsage = user.dailyTokenUsage;

        await refundTokens(amount, mappedReason, user, (updates) => {
            if (updates.dailyTokenUsage !== undefined) {
                updatedUsage = updates.dailyTokenUsage;
            }
        });

        // Also call systemRefund for legacy tracking if needed, or rely on the new transaction record
        // The old code called systemRefund. The new code records a transaction.
        // We should probably stick to the new transaction system.

        return {
            refunded: Math.max(0, (user.dailyTokenUsage || 0) - updatedUsage),
            newUsage: updatedUsage
        };
    }

    shouldRefund(error: any): { shouldRefund: boolean; reason: string } {
        return shouldRefund(error);
    }

    getUsageReport(user: User): ReturnType<typeof getTokenUsageReport> {
        return getTokenUsageReport(user);
    }

    getRefundHistory(userId: string): ReturnType<typeof getRefundHistory> {
        return getRefundHistory(userId);
    }

    getTotalRefunded(userId: string): number {
        return getTotalRefundedTokens(userId);
    }
}

export const tokenSystem = new UnifiedTokenSystem();

// Export individual functions for direct usage
export {
    chargeTokens,
    refundTokens,
    calculateCharge as calculateTokenCharge,
    loadTokenConfig,
    saveTokenConfig,
    getTokenConfig,
    getTokenUsageReport,
    getAnalytics as getTokenAnalytics,
    canSendMessage,
    getEffectiveTokenLimit,
    getTokenRefreshAmount,
    getTokenUsagePercentage,
    getTokenStatusText,
    shouldShowUpgradePrompt,
    calculateAttachmentTokenCost,
    analyzeComplexity,
    detectFeatures,
    shouldRefund,
    getRefundHistory,
    getTransactionHistory,
    getTotalRefundedTokens,
    shouldShowWarningModal,
    hasUnlimitedTokens,
    getTokenMultiplier
};
